import { Badge, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import {
  IconBan,
  IconBed,
  IconBuilding,
  IconCalendarCheck,
  IconChartLine,
  IconCheck,
  IconClock,
  IconCoin,
  IconCoins,
  IconCrown,
  IconDiamond,
  IconExclamationCircle,
  IconFlame,
  IconHeart,
  IconMoneybag,
  IconMoon,
  IconQuestionMark,
  IconShoppingCart,
  IconSun,
  IconTarget,
  IconTemperatureSnow,
  IconThumbUp,
  IconUser,
  IconUserCheck,
  IconUserX,
  IconWallet,
} from '@tabler/icons-react';
import { DASH, StatTile } from './ProfilesDetailsUi';

const getTimeBinInfo = (bin) => {
  if (bin === null || bin === undefined) {
    return {
      range: 'Unknown',
      label: 'No Data',
      icon: IconQuestionMark,
      advice: 'Data not available',
    };
  }

  const timeBins = {
    1: { range: '00:00-02:59', label: 'Late Night', icon: IconMoon, advice: 'Avoid contacting' },
    2: { range: '03:00-05:59', label: 'Early Morning', icon: IconSun, advice: 'Low response' },
    3: { range: '06:00-08:59', label: 'Morning', icon: IconSun, advice: 'Good for emails' },
    4: { range: '09:00-11:59', label: 'Late Morning', icon: IconClock, advice: 'Business hours' },
    5: { range: '12:00-14:59', label: 'Afternoon', icon: IconSun, advice: 'Peak activity' },
    6: { range: '15:00-17:59', label: 'Late Afternoon', icon: IconBuilding, advice: 'Excellent timing' },
    7: { range: '18:00-20:59', label: 'Evening', icon: IconMoon, advice: 'Prime time' },
    8: { range: '21:00-23:59', label: 'Night', icon: IconMoon, advice: 'Social media' },
  };
  return timeBins[bin] || timeBins[1];
};

const variantToColor = {
  success: 'green',
  info: 'blue',
  warning: 'yellow',
  danger: 'red',
  secondary: 'gray',
};

const getChurnInterpretation = (churnProb) => {
  if (churnProb === null || churnProb === undefined) {
    return {
      status: 'Risk of losing this customer',
      variant: 'secondary',
      icon: IconQuestionMark,
      action: 'No Data',
      description: 'Churn prediction not calculated yet',
    };
  }

  const churnPercentage = Math.round(churnProb * 100);
  if (churnPercentage <= 10) {
    return {
      status: 'Loyal Customer',
      variant: 'success',
      icon: IconHeart,
      action: 'Keep nurturing',
      description: `Only ${churnPercentage}% chance they'll leave - they love you!`,
    };
  } else if (churnPercentage <= 30) {
    return {
      status: 'Stable Customer',
      variant: 'info',
      icon: IconThumbUp,
      action: 'Continue engagement',
      description: `${churnPercentage}% chance of leaving - solid relationship`,
    };
  } else if (churnPercentage <= 60) {
    return {
      status: 'At Risk',
      variant: 'warning',
      icon: IconExclamationCircle,
      action: 'Increase touchpoints',
      description: `${churnPercentage}% chance of leaving - needs attention`,
    };
  } else {
    return {
      status: 'High Flight Risk',
      variant: 'danger',
      icon: IconExclamationCircle,
      action: 'Urgent retention campaign',
      description: `${churnPercentage}% chance of leaving - act now!`,
    };
  }
};

const getRetargetInterpretation = (retargetProb) => {
  if (retargetProb === null || retargetProb === undefined) {
    return {
      status: 'Response rate to ads',
      variant: 'secondary',
      icon: IconQuestionMark,
      roi: 'No Data',
      description: 'Retargeting prediction not available',
    };
  }

  const retargetPercentage = Math.round(retargetProb * 100);
  if (retargetPercentage >= 70) {
    return {
      status: 'Marketing Gold',
      variant: 'success',
      icon: IconTarget,
      roi: 'Excellent ROI',
      description: `${retargetPercentage}% likely to buy from ads - spend here!`,
    };
  } else if (retargetPercentage >= 40) {
    return {
      status: 'Good Target',
      variant: 'info',
      icon: IconCheck,
      roi: 'Positive ROI',
      description: `${retargetPercentage}% response rate - worth the investment`,
    };
  } else if (retargetPercentage >= 15) {
    return {
      status: 'Moderate Response',
      variant: 'warning',
      icon: IconChartLine,
      roi: 'Break-even ROI',
      description: `${retargetPercentage}% response - test small budgets`,
    };
  } else {
    return {
      status: 'Ad Resistant',
      variant: 'danger',
      icon: IconBan,
      roi: 'Poor ROI',
      description: `Only ${retargetPercentage}% response - save your budget`,
    };
  }
};

const getRFMStatus = (score, type) => {
  if (score === null || score === undefined) {
    return { label: 'No Data', icon: IconQuestionMark, variant: 'secondary' };
  }

  const statusMaps = {
    rfmRecency: {
      5: { label: 'Very Recent', icon: IconFlame, variant: 'success' },
      4: { label: 'Recently Active', icon: IconCalendarCheck, variant: 'info' },
      3: { label: 'Active', icon: IconUserCheck, variant: 'warning' },
      2: { label: 'Cooling Off', icon: IconTemperatureSnow, variant: 'warning' },
      1: { label: 'Dormant', icon: IconBed, variant: 'danger' },
    },
    rfmFrequency: {
      5: { label: 'VIP Buyer', icon: IconCrown, variant: 'success' },
      4: { label: 'Frequent Buyer', icon: IconShoppingCart, variant: 'info' },
      3: { label: 'Regular', icon: IconUser, variant: 'warning' },
      2: { label: 'Occasional', icon: IconClock, variant: 'warning' },
      1: { label: 'Rare', icon: IconUserX, variant: 'danger' },
    },
    rfmValue: {
      5: { label: 'Premium', icon: IconDiamond, variant: 'success' },
      4: { label: 'High Spender', icon: IconMoneybag, variant: 'info' },
      3: { label: 'Medium Spender', icon: IconCoin, variant: 'warning' },
      2: { label: 'Low Spender', icon: IconCoins, variant: 'warning' },
      1: { label: 'Minimal', icon: IconCoins, variant: 'danger' },
    },
  };
  return statusMaps[type][score] || statusMaps[type][1];
};

const getLTVDisplay = (ltv, currency) => {
  if (ltv === null || ltv === undefined) {
    return {
      value: 'No Data',
      icon: IconQuestionMark,
    };
  }

  return {
    value: `${ltv.toLocaleString(navigator.language, {
      // A domain without a configured currency would make Intl throw on
      // `style: 'currency'`, so fall back to a plain number.
      ...(currency ? { style: 'currency', currency } : {}),
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`,
    icon: IconWallet,
  };
};

// The insight fields are nullable, and an insights record that predates a field is
// missing it altogether — normalise both to null so every `=== null` check below sees
// "no data" rather than letting `undefined` through into the arithmetic.
const score = (value) => (value === null || value === undefined ? null : value);

const ProfileDetailsInsights = ({ profileInsights, currency }) => {
  const insights = profileInsights || {};
  const churnProbability = score(insights.churnProbability);
  const retargetPrediction = score(insights.retargetPrediction);
  const ltv = score(insights.ltv);
  const optimalTimeBin = score(insights.optimalTimeBin);

  const churnInfo = getChurnInterpretation(churnProbability);
  const retargetInfo = getRetargetInterpretation(retargetPrediction);
  const timeBinInfo = getTimeBinInfo(optimalTimeBin);
  const ltvDisplay = getLTVDisplay(ltv, currency);

  // Recency / frequency / value all read the same way — a score out of five with a
  // label — so they render from one list instead of three near-identical blocks.
  const rfm = [
    {
      field: 'rfmRecency',
      label: 'How recent?',
      hint: 'Time since their last purchase',
      score: score(insights.rfmRecency),
    },
    {
      field: 'rfmFrequency',
      label: 'How often?',
      hint: 'How frequently they buy',
      score: score(insights.rfmFrequency),
    },
    {
      field: 'rfmValue',
      label: 'How much?',
      hint: 'How much they spend',
      score: score(insights.rfmValue),
    },
  ].map((dimension) => ({ ...dimension, status: getRFMStatus(dimension.score, dimension.field) }));

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2, xl: 4 }} spacing="sm">
        <StatTile
          icon={churnInfo.icon}
          label="Loyalty score"
          color={churnProbability === null ? 'gray' : variantToColor[churnInfo.variant]}
          value={churnProbability === null ? DASH : `${100 - Math.round(churnProbability * 100)}%`}
          meter={churnProbability === null ? 0 : 100 - churnProbability * 100}
          hint={churnProbability === null ? churnInfo.description : `${churnInfo.status} · ${churnInfo.action}`}
        />
        <StatTile
          icon={retargetInfo.icon}
          label="Ad response rate"
          color={retargetPrediction === null ? 'gray' : variantToColor[retargetInfo.variant]}
          value={retargetPrediction === null ? DASH : `${Math.round(retargetPrediction * 100)}%`}
          meter={retargetPrediction === null ? 0 : retargetPrediction * 100}
          hint={retargetPrediction === null ? retargetInfo.description : `${retargetInfo.status} · ${retargetInfo.roi}`}
        />
        <StatTile
          icon={timeBinInfo.icon}
          label="Best contact time"
          color={optimalTimeBin === null ? 'gray' : 'blue'}
          value={optimalTimeBin === null ? DASH : timeBinInfo.label}
          hint={optimalTimeBin === null ? timeBinInfo.advice : `${timeBinInfo.range} · ${timeBinInfo.advice}`}
        />
        <StatTile
          icon={ltvDisplay.icon}
          label="Lifetime value"
          color={ltv === null ? 'gray' : 'blue'}
          value={ltv === null ? DASH : ltvDisplay.value}
          hint={ltv === null ? 'Not calculated yet' : 'Predicted spend over the whole relationship'}
        />
      </SimpleGrid>

      <div>
        <Group gap="xs" wrap="nowrap" mb="xs">
          <IconChartLine size={14} />
          <Text size="xs" fw={700} tt="uppercase" c="dimmed">
            Customer profile (RFM)
          </Text>
        </Group>
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
          {rfm.map((dimension) => (
            <StatTile
              key={dimension.field}
              icon={dimension.status.icon}
              label={dimension.label}
              color={dimension.score === null ? 'gray' : variantToColor[dimension.status.variant]}
              value={dimension.score === null ? DASH : dimension.status.label}
              badge={
                <Badge
                  size="sm"
                  variant="light"
                  color={dimension.score === null ? 'gray' : variantToColor[dimension.status.variant]}
                >
                  {dimension.score === null ? DASH : `${dimension.score}/5`}
                </Badge>
              }
              meter={dimension.score === null ? 0 : (dimension.score / 5) * 100}
              hint={dimension.hint}
            />
          ))}
        </SimpleGrid>
      </div>
    </Stack>
  );
};

export default ProfileDetailsInsights;
